import { fetchDatabaseSchema } from '@/lib/databases/api';
import {
  aggregateFunctionLabel,
  aggregateFunctionsForColumnType,
  DATE_FORMATS,
  DATE_FORMAT_LABELS,
  dateFormatFromConfig,
  defaultAggregateForColumnType,
  normalizeSelectOptions,
  relationTargetDatabaseId,
  rollupAggregateFunction,
  rollupRelationColumnId,
  rollupTargetPropertyId,
  SELECT_COLORS,
  type AggregateFunction,
  type DateFormat,
  type SelectColor,
  type SelectOption,
} from '@/lib/databases/values';
import { newBlockId } from '@/lib/blocks/ids';
import {
  searchLinkableDatabases,
  type LinkablePage,
} from '@/lib/blocks/page-link-search';
import { createElement, useEffect, useRef, useState } from 'react';

import type { DatabaseColumnDefinition } from '@/lib/databases/types';

import { SelectChip } from './SelectChip.web';

function defaultColorForIndex(index: number): SelectColor {
  return SELECT_COLORS[index % SELECT_COLORS.length] ?? 'gray';
}

function colorLabel(color: SelectColor): string {
  return color.charAt(0).toUpperCase() + color.slice(1);
}

export function ColumnPropertyEditor({
  column,
  allColumns = [],
  disabled,
  onSaveConfig,
}: {
  column: DatabaseColumnDefinition;
  allColumns?: DatabaseColumnDefinition[];
  disabled?: boolean;
  onSaveConfig: (config: Record<string, unknown>) => void;
}) {
  const [options, setOptions] = useState<SelectOption[]>(() =>
    normalizeSelectOptions(column.config),
  );
  const [dateFormat, setDateFormat] = useState<DateFormat>(() =>
    dateFormatFromConfig(column.config),
  );
  const [newName, setNewName] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const dragIndex = useRef<number | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const isRelationColumn = column.type === 'relation';
  const [dbQuery, setDbQuery] = useState('');
  const [dbResults, setDbResults] = useState<LinkablePage[]>([]);
  const [dbLoading, setDbLoading] = useState(false);
  const isRollupColumn = column.type === 'rollup';
  const [targetSchemaCols, setTargetSchemaCols] = useState<DatabaseColumnDefinition[]>([]);
  const [targetSchemaLoading, setTargetSchemaLoading] = useState(false);

  const relationColumns = allColumns.filter((c) => c.type === 'relation');
  const selectedRelationId = isRollupColumn ? rollupRelationColumnId(column.config) : null;
  const selectedTargetPropId = isRollupColumn ? rollupTargetPropertyId(column.config) : null;
  const selectedRelationCol = relationColumns.find((c) => c.id === selectedRelationId);
  const targetDbId = selectedRelationCol
    ? relationTargetDatabaseId(selectedRelationCol.config)
    : null;
  const targetCol = targetSchemaCols.find((c) => c.id === selectedTargetPropId);
  const rollupFn = isRollupColumn
    ? rollupAggregateFunction(column.config, targetCol?.type)
    : defaultAggregateForColumnType('text');
  const fnOptions =
    targetCol ? aggregateFunctionsForColumnType(targetCol.type) : aggregateFunctionsForColumnType('text');

  useEffect(() => {
    if (!isRollupColumn || !targetDbId) {
      setTargetSchemaCols([]);
      return;
    }
    let cancelled = false;
    setTargetSchemaLoading(true);
    void fetchDatabaseSchema(targetDbId)
      .then((schema) => {
        if (!cancelled) {
          setTargetSchemaCols(
            schema.columns.filter((c) => c.type !== 'rollup'),
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setTargetSchemaLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isRollupColumn, targetDbId, column.id]);

  useEffect(() => {
    setOptions(normalizeSelectOptions(column.config));
    setDateFormat(dateFormatFromConfig(column.config));
  }, [column.id, column.config]);

  useEffect(() => {
    if (!isRelationColumn) {
      return;
    }
    let cancelled = false;
    setDbLoading(true);
    void searchLinkableDatabases(dbQuery).then((pages) => {
      if (!cancelled) {
        setDbResults(pages);
        setDbLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isRelationColumn, dbQuery]);

  if (column.type === 'select' || column.type === 'multi_select') {
    const persist = (next: SelectOption[]) => {
      setOptions(next);
      onSaveConfig({ ...(column.config ?? {}), options: next });
    };

    const addOption = () => {
      const trimmed = newName.trim();
      if (!trimmed) {
        return;
      }
      persist([
        ...options,
        {
          id: newBlockId(),
          name: trimmed,
          color: defaultColorForIndex(options.length),
        },
      ]);
      setNewName('');
    };

    const reorder = (from: number, to: number) => {
      if (from === to || from < 0 || to < 0 || from >= options.length || to >= options.length) {
        return;
      }
      const next = [...options];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);
      persist(next);
    };

    const optionItems = options.map((opt, index) => {
      const isExpanded = expandedId === opt.id;
      const row = createElement(
        'div',
        {
          className: [
            'cs-db-option-row',
            dragOverId === opt.id ? 'cs-db-option-row-dragover' : '',
          ]
            .filter(Boolean)
            .join(' '),
          draggable: !disabled,
          onDragStart: () => {
            dragIndex.current = index;
          },
          onDragOver: (e: DragEvent) => {
            e.preventDefault();
            if (dragOverId !== opt.id) {
              setDragOverId(opt.id);
            }
          },
          onDragLeave: () => {
            if (dragOverId === opt.id) {
              setDragOverId(null);
            }
          },
          onDrop: (e: DragEvent) => {
            e.preventDefault();
            if (dragIndex.current !== null) {
              reorder(dragIndex.current, index);
            }
            dragIndex.current = null;
            setDragOverId(null);
          },
          onDragEnd: () => {
            dragIndex.current = null;
            setDragOverId(null);
          },
        },
        createElement(
          'span',
          {
            className: 'cs-db-option-handle',
            'aria-hidden': true,
            title: 'Drag to reorder',
          },
          '⠿',
        ),
        createElement(
          'button',
          {
            type: 'button',
            className: 'cs-db-option-main',
            disabled,
            onClick: () => setExpandedId(isExpanded ? null : opt.id),
            onMouseDown: (e: MouseEvent) => e.stopPropagation(),
          },
          createElement(SelectChip, { label: opt.name, color: opt.color }),
          createElement(
            'span',
            {
              className: [
                'cs-db-option-chevron',
                isExpanded ? 'cs-db-option-chevron-open' : '',
              ]
                .filter(Boolean)
                .join(' '),
            },
            '›',
          ),
        ),
      );

      const detail = isExpanded
        ? createElement(
            'div',
            { className: 'cs-db-option-detail' },
            createElement('input', {
              type: 'text',
              className: 'cs-db-option-name-input',
              value: opt.name,
              autoFocus: true,
              disabled,
              onChange: (e: Event) => {
                const name = (e.target as HTMLInputElement).value;
                persist(options.map((o) => (o.id === opt.id ? { ...o, name } : o)));
              },
              onMouseDown: (e: MouseEvent) => e.stopPropagation(),
            }),
            createElement(
              'button',
              {
                type: 'button',
                className: 'cs-db-option-delete-btn',
                disabled,
                onClick: () => {
                  setExpandedId(null);
                  persist(options.filter((o) => o.id !== opt.id));
                },
                onMouseDown: (e: MouseEvent) => e.stopPropagation(),
              },
              createElement('span', { className: 'cs-db-option-delete-icon' }, '🗑'),
              'Delete',
            ),
            createElement(
              'p',
              { className: 'cs-db-option-colors-label' },
              'Colors',
            ),
            ...SELECT_COLORS.map((c) =>
              createElement(
                'button',
                {
                  key: c,
                  type: 'button',
                  className: 'cs-db-option-color-row',
                  disabled,
                  onClick: () =>
                    persist(
                      options.map((o) => (o.id === opt.id ? { ...o, color: c } : o)),
                    ),
                  onMouseDown: (e: MouseEvent) => e.stopPropagation(),
                },
                createElement('span', {
                  className: `cs-db-option-swatch cs-db-swatch-${c}`,
                }),
                createElement('span', { className: 'cs-db-option-color-name' }, colorLabel(c)),
                opt.color === c
                  ? createElement('span', { className: 'cs-db-option-color-check' }, '✓')
                  : null,
              ),
            ),
          )
        : null;

      return createElement(
        'div',
        { key: opt.id, className: 'cs-db-option-item' },
        row,
        detail,
      );
    });

    return createElement(
      'div',
      { className: 'cs-db-option-manager' },
      createElement(
        'div',
        { className: 'cs-db-option-manager-header' },
        createElement('span', { className: 'cs-db-col-property-hint' }, 'Options'),
        createElement(
          'button',
          {
            type: 'button',
            className: 'cs-db-option-add-btn',
            disabled,
            title: 'Add option',
            onClick: addOption,
            onMouseDown: (e: MouseEvent) => e.stopPropagation(),
          },
          '+',
        ),
      ),
      createElement('div', { className: 'cs-db-option-list' }, ...optionItems),
      createElement('input', {
        type: 'text',
        className: 'cs-db-option-new-input',
        placeholder: 'New option',
        value: newName,
        disabled,
        onChange: (e: Event) => setNewName((e.target as HTMLInputElement).value),
        onKeyDown: (e: KeyboardEvent) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            addOption();
          }
        },
        onMouseDown: (e: MouseEvent) => e.stopPropagation(),
      }),
    );
  }

  if (column.type === 'relation') {
    const targetId = relationTargetDatabaseId(column.config);

    return createElement(
      'div',
      { className: 'cs-db-col-property-editor' },
      createElement('label', { className: 'cs-db-col-property-label' }, 'Connect to'),
      createElement('input', {
        type: 'search',
        className: 'cs-db-option-new-input',
        placeholder: 'Search databases…',
        value: dbQuery,
        disabled,
        onChange: (e: Event) => setDbQuery((e.target as HTMLInputElement).value),
        onMouseDown: (e: MouseEvent) => e.stopPropagation(),
      }),
      dbLoading
        ? createElement('p', { className: 'cs-db-col-property-hint' }, 'Loading…')
        : createElement(
            'ul',
            { className: 'cs-db-relation-db-list' },
            dbResults.map((page) =>
              createElement(
                'li',
                { key: page.id },
                createElement(
                  'button',
                  {
                    type: 'button',
                    className: [
                      'cs-db-relation-db-item',
                      targetId === page.id ? 'cs-db-relation-db-item-active' : '',
                    ]
                      .filter(Boolean)
                      .join(' '),
                    disabled,
                    onClick: () =>
                      onSaveConfig({
                        ...(column.config ?? {}),
                        databaseId: page.id,
                      }),
                    onMouseDown: (e: MouseEvent) => e.stopPropagation(),
                  },
                  page.title,
                  page.breadcrumb
                    ? createElement(
                        'span',
                        { className: 'cs-db-relation-db-breadcrumb' },
                        page.breadcrumb,
                      )
                    : null,
                ),
              ),
            ),
          ),
      targetId
        ? createElement(
            'p',
            { className: 'cs-db-col-property-hint' },
            'A reciprocal relation column is created on the target database automatically.',
          )
        : null,
    );
  }

  if (column.type === 'rollup') {
    const saveRollup = (patch: Record<string, unknown>) => {
      onSaveConfig({ ...(column.config ?? {}), ...patch });
    };

    return createElement(
      'div',
      { className: 'cs-db-col-property-editor' },
      createElement('label', { className: 'cs-db-col-property-label' }, 'Relation'),
      relationColumns.length === 0
        ? createElement(
            'p',
            { className: 'cs-db-col-property-hint' },
            'Add a relation column first.',
          )
        : createElement(
            'select',
            {
              className: 'cs-db-col-date-format',
              value: selectedRelationId ?? '',
              disabled,
              onChange: (e: Event) => {
                const relationColumnId = (e.target as HTMLSelectElement).value;
                saveRollup({
                  relationColumnId,
                  targetPropertyId: undefined,
                  function: undefined,
                });
              },
              onMouseDown: (e: MouseEvent) => e.stopPropagation(),
            },
            createElement('option', { value: '' }, 'Select relation…'),
            relationColumns.map((c) =>
              createElement('option', { key: c.id, value: c.id }, c.name),
            ),
          ),
      selectedRelationId
        ? createElement(
            'div',
            { className: 'cs-db-rollup-target-section' },
            createElement('label', { className: 'cs-db-col-property-label' }, 'Property'),
            targetSchemaLoading
              ? createElement('p', { className: 'cs-db-col-property-hint' }, 'Loading…')
              : createElement(
                  'select',
                  {
                    className: 'cs-db-col-date-format',
                    value: selectedTargetPropId ?? '',
                    disabled: disabled || targetSchemaCols.length === 0,
                    onChange: (e: Event) => {
                      const targetPropertyId = (e.target as HTMLSelectElement).value;
                      const col = targetSchemaCols.find((c) => c.id === targetPropertyId);
                      saveRollup({
                        targetPropertyId,
                        function: defaultAggregateForColumnType(col?.type ?? 'text'),
                      });
                    },
                    onMouseDown: (e: MouseEvent) => e.stopPropagation(),
                  },
                  createElement('option', { value: '' }, 'Select property…'),
                  targetSchemaCols.map((c) =>
                    createElement('option', { key: c.id, value: c.id }, c.name),
                  ),
                ),
          )
        : null,
      selectedTargetPropId
        ? createElement(
            'div',
            { className: 'cs-db-rollup-fn-section' },
            createElement('label', { className: 'cs-db-col-property-label' }, 'Calculate'),
            createElement(
              'select',
              {
                className: 'cs-db-col-date-format',
                value: rollupFn,
                disabled,
                onChange: (e: Event) => {
                  saveRollup({ function: (e.target as HTMLSelectElement).value as AggregateFunction });
                },
                onMouseDown: (e: MouseEvent) => e.stopPropagation(),
              },
              fnOptions.map((f) =>
                createElement('option', { key: f, value: f }, aggregateFunctionLabel(f)),
              ),
            ),
          )
        : null,
    );
  }

  if (column.type === 'date') {
    return createElement(
      'div',
      { className: 'cs-db-col-property-editor' },
      createElement('label', { className: 'cs-db-col-property-label' }, 'Date format'),
      createElement(
        'select',
        {
          className: 'cs-db-col-date-format',
          value: dateFormat,
          disabled,
          onChange: (e: Event) => {
            const next = (e.target as HTMLSelectElement).value as DateFormat;
            setDateFormat(next);
            onSaveConfig({ ...(column.config ?? {}), dateFormat: next });
          },
          onMouseDown: (e: MouseEvent) => e.stopPropagation(),
        },
        DATE_FORMATS.map((f) =>
          createElement('option', { key: f, value: f }, DATE_FORMAT_LABELS[f]),
        ),
      ),
    );
  }

  return createElement(
    'p',
    { className: 'cs-db-col-property-hint' },
    'No property settings for this column type.',
  );
}
